import { AlertTriangle, CameraOff, Loader2, type LucideIcon, RefreshCw } from "lucide-react";
import * as React from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { cn } from "@/shared/lib/utils";

export type ProviderCaptureMode = "document" | "selfie" | "company";
export type ProviderCaptureState = "loading" | "active" | "error" | "denied";

export interface ProviderCaptureHint {
  /** Leading micro-icon for the hint chip. Icons are passed as props, never as children. */
  icon: LucideIcon;
  label: string;
}

export interface ProviderCaptureFrameProps extends React.ComponentPropsWithoutRef<"div"> {
  /** Guide overlay: rectangular (`document`/`company`) vs circular (`selfie`). */
  mode?: ProviderCaptureMode;
  /** Externally controlled state of the embedded SDK — never an opaque internal state. */
  state?: ProviderCaptureState;
  /** Heading above the frame (e.g. "Documento de identidad"). i18n string from the consumer. */
  title?: string;
  /** Short instruction under the title. */
  instruction?: string;
  /** Hints rendered as chips below the frame. */
  hints?: ProviderCaptureHint[];
  /** Label shown while the SDK is loading. */
  loadingLabel?: string;
  /** Copy for the `error` state. */
  errorTitle?: string;
  errorDescription?: string;
  /** Copy for the `denied` (camera permission blocked) state. */
  deniedTitle?: string;
  deniedDescription?: string;
  /** Label for the recovery action shown on `error`/`denied`. */
  actionLabel?: string;
  /** Invoked when the user clicks the recovery action. */
  onAction?: () => void;
  /** Icon for the recovery action (defaults to a refresh glyph). */
  actionIcon?: LucideIcon;
  /** The provider SDK mounts here when `state === "active"`. */
  children?: React.ReactNode;
}

interface CaptureStatusBlockProps {
  icon: LucideIcon;
  title?: string;
  description?: string;
  actionLabel?: string;
  actionIcon: LucideIcon;
  onAction?: () => void;
}

function CaptureStatusBlock({
  icon: Icon,
  title,
  description,
  actionLabel,
  actionIcon,
  onAction,
}: CaptureStatusBlockProps) {
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6 text-center">
      <span className="flex size-12 items-center justify-center rounded-full bg-background/80 text-muted-foreground">
        <Icon className="size-6" aria-hidden="true" />
      </span>
      <div className="space-y-1">
        {title ? <p className="text-sm font-medium text-foreground">{title}</p> : null}
        {description ? <p className="text-xs text-muted-foreground">{description}</p> : null}
      </div>
      {onAction && actionLabel ? (
        <ColocaButton variant="secondary" size="sm" icon={actionIcon} onClick={onAction}>
          {actionLabel}
        </ColocaButton>
      ) : null}
    </div>
  );
}

function CaptureGuide({ mode }: { mode: ProviderCaptureMode }) {
  if (mode === "selfie") {
    return (
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <div className="aspect-square h-3/5 rounded-full border-2 border-dashed border-primary/60" />
      </div>
    );
  }
  return (
    <div className="pointer-events-none absolute inset-6">
      <span className="absolute left-0 top-0 size-8 rounded-tl-lg border-l-2 border-t-2 border-primary/70" />
      <span className="absolute right-0 top-0 size-8 rounded-tr-lg border-r-2 border-t-2 border-primary/70" />
      <span className="absolute bottom-0 left-0 size-8 rounded-bl-lg border-b-2 border-l-2 border-primary/70" />
      <span className="absolute bottom-0 right-0 size-8 rounded-br-lg border-b-2 border-r-2 border-primary/70" />
    </div>
  );
}

/**
 * Branded container where the external verification SDK is mounted (capture stays inside Coloca — the link
 * never redirects to the provider). This component draws only the branded frame, guide overlay and its
 * states; it does NOT draw the real camera. The SDK is inserted into `children` when `state === "active"`.
 *
 * The only new design-system component of the verification engine — built to the DS API checklist: forwards
 * `ref`, spreads `...rest`, merges `className` with `cn()`, takes icons as props, and the state / mode are
 * fully controlled by props.
 *
 * @summary Branded mount point + states (loading / active / error / denied) for the embedded capture SDK.
 */
export const ProviderCaptureFrame = React.forwardRef<HTMLDivElement, ProviderCaptureFrameProps>(
  function ProviderCaptureFrame(
    {
      mode = "document",
      state = "loading",
      title,
      instruction,
      hints,
      loadingLabel,
      errorTitle,
      errorDescription,
      deniedTitle,
      deniedDescription,
      actionLabel,
      onAction,
      actionIcon = RefreshCw,
      className,
      children,
      ...rest
    },
    ref,
  ) {
    return (
      <div ref={ref} className={cn("flex w-full flex-col gap-4", className)} {...rest}>
        {title || instruction ? (
          <div className="space-y-1 text-center">
            {title ? <h3 className="text-base font-semibold text-foreground">{title}</h3> : null}
            {instruction ? <p className="text-sm text-muted-foreground">{instruction}</p> : null}
          </div>
        ) : null}

        {state === "active" ? (
          // Active: the embedded SDK drives its own UI (no guide overlay, flexible height).
          <div className="mx-auto w-full max-w-[480px] overflow-hidden rounded-2xl border border-border bg-muted">
            <div className="min-h-[560px] w-full">{children}</div>
          </div>
        ) : (
          // Loading / error / denied: branded placeholder with the guide overlay.
          <div className="relative mx-auto aspect-[3/4] w-full max-w-[360px] overflow-hidden rounded-2xl border border-border bg-muted">
            <div role="status" aria-live="polite" className="absolute inset-0">
              {state === "loading" ? (
                <>
                  <ColocaSkeleton className="h-full w-full rounded-none" />
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="size-5 animate-spin" aria-hidden="true" />
                    {loadingLabel ? <span>{loadingLabel}</span> : null}
                  </div>
                </>
              ) : null}

              {state === "error" ? (
                <CaptureStatusBlock
                  icon={AlertTriangle}
                  title={errorTitle}
                  description={errorDescription}
                  actionLabel={actionLabel}
                  actionIcon={actionIcon}
                  onAction={onAction}
                />
              ) : null}

              {state === "denied" ? (
                <CaptureStatusBlock
                  icon={CameraOff}
                  title={deniedTitle}
                  description={deniedDescription}
                  actionLabel={actionLabel}
                  actionIcon={actionIcon}
                  onAction={onAction}
                />
              ) : null}
            </div>

            {state === "loading" ? <CaptureGuide mode={mode} /> : null}
          </div>
        )}

        {hints && hints.length > 0 ? (
          <ul className="flex flex-wrap justify-center gap-2">
            {hints.map((hint) => {
              const HintIcon = hint.icon;
              return (
                <li
                  key={hint.label}
                  className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1 text-xs text-muted-foreground"
                >
                  <HintIcon className="size-3.5 shrink-0" aria-hidden="true" />
                  {hint.label}
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
    );
  },
);
